import Axios, { AxiosError, AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { apiInstance } from "./apiInstance";
import { api } from "./AuthApi";
import { parseJwt } from "../lib/parseToken";
import { publicPaths } from "@/lib/utils";
import { getAuthToken, setAuthCookies, removeAuthCookies, getRefreshToken } from "@/lib/authCookies";
import { disconnectChatSocket, reconnectChatSocket } from "@/lib/chatSocket";
import { disconnectNotificationSocket, reconnectNotificationSocket } from "@/lib/notificationSocket";

/* ---------------- TOKEN ACTIONS ---------------- */

export function getToken() {
  const headerToken = apiInstance.defaults.headers.common["Authorization"];
  if (typeof headerToken === "string") return headerToken.split(" ")[1];
  const storedToken = getAuthToken();
  if (storedToken) apiInstance.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
  return storedToken ?? null;
}

export function setToken(token: string, refreshToken?: string) {
  apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  setAuthCookies(token, refreshToken);
  reconnectChatSocket(token);
  reconnectNotificationSocket(token);
}

export function removeToken() {
  delete apiInstance.defaults.headers.common["Authorization"];
  removeAuthCookies();
  disconnectChatSocket();
  disconnectNotificationSocket();
}

/* ---------------- TOKEN VALIDATION ---------------- */

const now = () => Math.floor(Date.now() / 1000);

export function isTokenValid() {
  try {
    const jwt = parseJwt(getToken() ?? "");
    return !!jwt?.exp && now() < jwt.exp;
  } catch { return false; }
}

export function isRefreshTokenValid() {
  try {
    const jwt = parseJwt(getRefreshToken() ?? "");
    return !jwt?.exp || now() < jwt.exp;
  } catch { return true; }
}

export function validateToken() {
  if (isTokenValid()) return true;
  logoutUser();
  return false;
}

/* ---------------- REFRESH TOKEN ---------------- */

let refreshPromise: Promise<void> | null = null;

export async function checkAndRefreshToken() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return;

  const jwt = parseJwt(getToken() ?? "");
  if (jwt?.exp && jwt.exp - now() > 60) return;

  refreshPromise = apiInstance
    .post(api.generate_refresh_token, { refreshToken })
    .then(({ data }) => {
      const { token: newToken, refreshToken: newRefresh } = data?.data?.data ?? {};
      if (newToken) setToken(newToken, newRefresh);
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
}

/* ---------------- LOGOUT ---------------- */
export function logoutUser() {
  removeToken();
  window.location.href = "/login";
}

/* ---------------- AXIOS INTERCEPTORS ---------------- */
export async function handleRequest(request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  request.headers = request.headers instanceof AxiosHeaders ? request.headers : new AxiosHeaders(request.headers);
  if (typeof window !== "undefined" && publicPaths.some((path) => window.location.pathname.includes(path))) return request;

  if (request.url?.includes(api.generate_refresh_token)) return request;

  if (!isTokenValid() && !isRefreshTokenValid()) {
    logoutUser();
    return Promise.reject(new Axios.Cancel("Session expired"));
  }

  if (isRefreshTokenValid()) await checkAndRefreshToken();

  const token = getToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }

  return request;
}

export function handleResponse(response: AxiosResponse): AxiosResponse {
  return response;
}

export async function handleApiError(error: AxiosError) {
  if (Axios.isCancel(error)) throw error;
  if (!error.response) throw error;

  if (error.response.status === 401) logoutUser();

  throw error;
}
